import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, User, ChevronRight, Twitter, Send, RefreshCw, Loader2, Lock, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { getEditions, getEditionById } from '../services/api';
import * as api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ShareButtons from '../components/ShareButtons';

const SERIF = "'Libre Baskerville', Georgia, serif";
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const getImg = (src) => {
  if (!src) return null;
  return src.startsWith('/api/') ? `${API_BASE}${src}` : src;
};

const COUNTRIES = [
  "Argentina","Bolivia","Brasil","Chile","Colombia","Costa Rica","Cuba","Ecuador",
  "El Salvador","España","Estados Unidos","Guatemala","Honduras","México","Nicaragua",
  "Panamá","Paraguay","Perú","Puerto Rico","República Dominicana","Uruguay","Venezuela",
  "Otro"
];

/* ─── REGISTRATION WALL ─── */
const RegistrationWall = () => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', country: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.country) {
      setError('Todos los campos son requeridos');
      return;
    }
    setLoading(true); setError('');
    try {
      const res = await api.register(form);
      if (res.token) {
        authLogin(res.token, res.user);
        navigate(0); // Reload to show all articles
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrarse');
    } finally { setLoading(false); }
  };

  return (
    <div className="relative">
      <div className="py-10 px-4 text-center bg-white">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-black mb-2" style={{ fontFamily: SERIF }}>
            Sigue leyendo
          </h3>
          <p className="text-[#555] text-sm mb-6">
            Regístrate gratis para leer el artículo completo
          </p>

          {!showForm ? (
            <Button onClick={() => setShowForm(true)} className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-3 text-base font-bold">
              Registrarse gratis
            </Button>
          ) : (
            <form onSubmit={handleRegister} className="space-y-3 text-left bg-white rounded-xl border border-gray-200 p-6 shadow-lg">
              {error && <p className="text-sm text-red-600 text-center">{error}</p>}
              <div>
                <label className="block text-xs font-semibold text-[#555] mb-1">Nombre completo</label>
                <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Tu nombre" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#555] mb-1">Correo electrónico</label>
                <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="correo@ejemplo.com" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#555] mb-1">Contraseña</label>
                <Input type="password" value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} placeholder="Mínimo 6 caracteres" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#555] mb-1">País</label>
                <Select value={form.country} onValueChange={v => setForm(p => ({...p, country: v}))}>
                  <SelectTrigger><SelectValue placeholder="Selecciona tu país" /></SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-3">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Crear cuenta gratis
              </Button>
              <p className="text-xs text-center text-[#999]">
                ¿Ya tienes cuenta? <Link to="/login" className="text-cyan-500 font-medium">Inicia sesión</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── COVER ─── */
const EditionCover = ({ edition, size = 'md' }) => {
  const img = getImg(edition?.cover_image);
  const title = edition?.title || '';
  const h = size === 'sm' ? 'max-h-[320px]' : size === 'lg' ? 'max-h-[700px]' : 'max-h-[500px]';
  return (
    <div className={`relative w-full bg-black overflow-hidden rounded-xl ${h}`} style={{ aspectRatio: '3/4' }}>
      {img && <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70" />
      <div className="absolute top-4 left-0 right-0 z-10 text-center">
        <span className="text-sm font-bold tracking-[0.15em] text-white">LA<span className="text-cyan-400">TECHNO</span>LOGÍA</span>
        <div className="h-px w-16 mx-auto mt-1.5 bg-white/40" />
        <p className="text-[9px] tracking-[0.3em] mt-1 text-white/60 uppercase">Edición Semanal</p>
      </div>
      <div className="absolute bottom-0 left-0 right-0 z-10 p-4">
        <h1 className="text-base sm:text-lg font-bold text-white leading-tight" style={{ fontFamily: SERIF }}>{title}</h1>
      </div>
    </div>
  );
};

/* ─── ARTICLE CARD ─── */
const ArticleCard = ({ section, index, editionId }) => {
  const img = getImg(section.image);
  const isOpinion = section.type === 'opinion';

  return (
    <Link to={`/edition/${editionId}/article/${index}`} className="group block" data-testid={`edition-article-${index}`}>
      <div className="aspect-[4/3] overflow-hidden rounded-lg bg-gray-100 mb-3">
        {img ? <img src={img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /> : <div className="w-full h-full bg-gray-200 flex items-center justify-center"><BookOpen className="w-8 h-8 text-gray-400" /></div>}
      </div>
      {isOpinion && <span className="text-[10px] font-bold uppercase tracking-wider text-[#e62d28]">Opinión</span>}
      <h3 className="text-[15px] font-bold text-black group-hover:text-[#e62d28] transition-colors leading-snug line-clamp-2 mt-1" style={{ fontFamily: SERIF }}>{section.title}</h3>
      <p className="text-xs text-[#555] mt-1.5 flex items-center gap-1.5"><User className="w-3 h-3" /> {section.author}</p>
    </Link>
  );
};

/* ═══ EDITIONS LIST ═══ */
export const EditionsListPage = () => {
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { getEditions().then(d => setEditions(d || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="min-h-screen bg-[#f8f8f8] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-black border-t-transparent" /></div>;
  const latest = editions.find(e => e.status === 'published') || editions[0];
  const others = editions.filter(e => e.edition_id !== latest?.edition_id);

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      <div className="max-w-[1200px] mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <h1 className="text-sm font-bold uppercase tracking-[0.2em] text-black mb-8">Edición Semanal</h1>
        {latest ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-3">
              <Link to={`/edition/${latest.edition_id}`}><EditionCover edition={latest} /></Link>
            </div>
            <div className="lg:col-span-9">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-black pb-3 border-b-2 border-black mb-4">En esta edición</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {(latest.sections || []).map((s, i) => (
                  <ArticleCard key={i} section={s} index={i} editionId={latest.edition_id} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border p-12 text-center"><BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-[#555]">No hay ediciones</p></div>
        )}
        {others.length > 0 && (
          <div>
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-black pb-3 border-b border-gray-200 mb-4">Ediciones anteriores</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {others.map(e => (
                <Link key={e.edition_id} to={`/edition/${e.edition_id}`} className="group">
                  <EditionCover edition={e} size="sm" />
                  <p className="text-xs text-[#555] mt-2">Semana {e.week_number}, {e.year}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ═══ EDITION DETAIL ═══ */
export const EditionDetailPage = () => {
  const { editionId } = useParams();
  const [edition, setEdition] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { getEditionById(editionId).then(setEdition).catch(() => {}).finally(() => setLoading(false)); }, [editionId]);

  if (loading) return <div className="min-h-screen bg-[#f8f8f8] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-black border-t-transparent" /></div>;
  if (!edition) return <div className="min-h-screen bg-[#f8f8f8] flex items-center justify-center"><p>Edición no encontrada</p></div>;

  const sections = edition.sections || [];

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      <div className="bg-black py-8">
        <div className="max-w-[1100px] mx-auto px-4 lg:px-8">
          <Link to="/edition" className="text-gray-400 hover:text-white text-sm flex items-center gap-1 mb-6"><ArrowLeft className="w-4 h-4" /> Ediciones</Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-4 max-w-[300px] mx-auto lg:mx-0"><EditionCover edition={edition} /></div>
            <div className="lg:col-span-8 text-white">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-2">Semana {edition.week_number}, {edition.year}</p>
              <h1 className="text-2xl lg:text-3xl font-bold leading-tight mb-3" style={{ fontFamily: SERIF }}>{edition.title}</h1>
              {edition.summary && <p className="text-gray-400 text-sm leading-relaxed">{edition.summary}</p>}
              <p className="text-gray-500 text-xs mt-4">{sections.length} artículos en esta edición</p>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-[1100px] mx-auto px-4 lg:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map((s, i) => <ArticleCard key={i} section={s} index={i} editionId={editionId} />)}
        </div>
      </div>
    </div>
  );
};

/* ═══ INDIVIDUAL ARTICLE ═══ */
export const EditionArticlePage = () => {
  const { editionId, articleIndex } = useParams();
  const { user } = useAuth();
  const [edition, setEdition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tweetModal, setTweetModal] = useState(null);

  useEffect(() => { getEditionById(editionId).then(setEdition).catch(() => {}).finally(() => setLoading(false)); }, [editionId]);

  const isAdmin = user && (user.user_type === 'admin' || user.staff_role === 'admin');
  const handleOpenTweet = (article, idx) => {
    const url = `https://latechnologia.com/edition/${editionId}/article/${idx}`;
    setTweetModal({ tweetText: `${article.title}\n\n#LaTecnologia #EdiciónSemanal\n${url}`, loading: false, idx });
  };
  const handlePublishTweet = async () => {
    if (!tweetModal) return;
    setTweetModal(prev => ({ ...prev, loading: true }));
    try {
      const res = await api.publishEditionArticleTweet(editionId, tweetModal.idx, tweetModal.tweetText);
      if (res.success) { alert('Tweet publicado'); setTweetModal(null); }
      else { alert('Error: ' + (res.error || '')); setTweetModal(prev => ({ ...prev, loading: false })); }
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
      setTweetModal(prev => ({ ...prev, loading: false }));
    }
  };

  if (loading) return <div className="min-h-screen bg-[#f8f8f8] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-black border-t-transparent" /></div>;
  if (!edition) return <div className="min-h-screen bg-[#f8f8f8] flex items-center justify-center"><p>Artículo no encontrado</p></div>;

  const idx = parseInt(articleIndex, 10);
  const sections = edition.sections || [];
  const article = sections[idx];
  if (!article) return <div className="min-h-screen bg-[#f8f8f8] flex items-center justify-center"><p>Artículo no disponible. <Link to={`/edition/${editionId}`} className="text-[#e62d28]">Ver edición</Link></p></div>;

  const img = getImg(article.image);
  const isOpinion = article.type === 'opinion';
  const prevArticle = idx > 0 ? sections[idx - 1] : null;
  const nextArticle = idx < sections.length - 1 ? sections[idx + 1] : null;

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-[#f8f8f8] border-b border-[#dddddd]">
        <div className="max-w-[800px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-[#555]">
            <Link to="/edition" className="hover:text-black">Ediciones</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`/edition/${editionId}`} className="hover:text-black line-clamp-1">{edition.title}</Link>
          </div>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => handleOpenTweet(article, idx)} className="text-xs">
              <Twitter className="w-3 h-3 mr-1" /> Tweet
            </Button>
          )}
        </div>
      </div>
      {img && <div className="w-full max-h-[450px] overflow-hidden"><img src={img} alt="" className="w-full h-full object-cover" style={{ maxHeight: '450px' }} /></div>}
      <article className="max-w-[800px] mx-auto px-4 py-8 lg:py-12">
        {isOpinion && <span className="text-[11px] font-bold uppercase tracking-wider text-[#e62d28] mb-3 block">Opinión</span>}
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-black leading-tight mb-4" style={{ fontFamily: SERIF }}>{article.title}</h1>
        <div className="flex items-center gap-3 text-sm text-[#555] mb-8 pb-6 border-b border-[#dddddd]">
          <User className="w-4 h-4" />
          <span>Por <strong className="text-black">{article.author}</strong></span>
          <span className="text-[#ccc]">|</span>
          <span>Edición Semanal #{edition.week_number}</span>
        </div>
        <div className="prose prose-lg max-w-none text-[#333] leading-[1.8]" style={{ fontFamily: SERIF, fontSize: '17px' }} dangerouslySetInnerHTML={{ __html: article.content }} />
        
        {/* Share buttons (show even on truncated articles) */}
        <ShareButtons 
          url={`https://latechnologia.com/edition/${editionId}/article/${idx}`}
          title={article.title}
        />
        
        {/* Blur overlay + Registration wall */}
        {article.truncated && !user && (
          <div className="relative mt-0">
            {/* Blurred fake content */}
            <div className="select-none pointer-events-none" style={{ filter: 'blur(6px)', WebkitFilter: 'blur(6px)' }}>
              <p style={{ fontFamily: SERIF, fontSize: '17px', lineHeight: '1.8', color: '#333' }}>
                La convergencia de estas fuerzas tecnológicas plantea preguntas fundamentales sobre el futuro de la innovación. 
                Los analistas señalan que estamos ante un punto de inflexión donde las decisiones tomadas hoy determinarán 
                la arquitectura digital de las próximas décadas. Las implicaciones se extienden más allá del sector tecnológico, 
                afectando la geopolítica, la economía global y la forma en que las sociedades se organizan y comunican.
              </p>
            </div>
            {/* White overlay that covers blur completely */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/80 to-white" />
            {/* Registration CTA on solid white */}
            <div className="relative bg-white">
              <RegistrationWall />
            </div>
          </div>
        )}
      </article>

      {/* Nav between articles */}
      <div className="bg-[#f8f8f8] border-t border-[#dddddd]">
        <div className="max-w-[800px] mx-auto px-4 py-8">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-black mb-4">Más en esta edición</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {prevArticle && (
              <Link to={`/edition/${editionId}/article/${idx - 1}`} className="group flex gap-3 items-start">
                {getImg(prevArticle.image) && <img src={getImg(prevArticle.image)} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
                <div><p className="text-xs text-[#555]">Anterior</p><p className="text-sm font-bold text-black group-hover:text-[#e62d28] line-clamp-2" style={{ fontFamily: SERIF }}>{prevArticle.title}</p></div>
              </Link>
            )}
            {nextArticle && (
              <Link to={`/edition/${editionId}/article/${idx + 1}`} className="group flex gap-3 items-start sm:ml-auto text-right">
                <div><p className="text-xs text-[#555]">Siguiente</p><p className="text-sm font-bold text-black group-hover:text-[#e62d28] line-clamp-2" style={{ fontFamily: SERIF }}>{nextArticle.title}</p></div>
                {getImg(nextArticle.image) && <img src={getImg(nextArticle.image)} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Tweet Modal */}
      {tweetModal && (
        <Dialog open={true} onOpenChange={() => setTweetModal(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Twitter className="w-5 h-5" /> Publicar en X</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <Textarea value={tweetModal.tweetText} onChange={e => setTweetModal(prev => ({ ...prev, tweetText: e.target.value }))} rows={5} disabled={tweetModal.loading} />
              <p className="text-xs text-[#555]">{tweetModal.tweetText?.length || 0}/280</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTweetModal(null)}>Cancelar</Button>
              <Button onClick={handlePublishTweet} disabled={tweetModal.loading} className="bg-black text-white hover:bg-gray-800">
                {tweetModal.loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Publicar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default EditionsListPage;
